import type { DeleteBlocker } from '../types/phoneNumber.types'

/**
 * Route path templates for the admin3 pages that correspond to each blocker
 * resource type. Supplied by the host app so the library stays decoupled from
 * the app's route constants. Values may use `:pbxId`, `:groupId`, `:id`, or
 * `:extensionDialPlan` placeholders — each is filled with the matching field
 * from the blocker entry.
 *
 * A missing key means "no link available" for that resource type, in which
 * case {@link buildBlockerLink} returns `null` and the FE renders the
 * blocker name as plain text.
 */
export interface BlockerRoutePaths {
  /** `/pbxes/:pbxId/messaging/:groupId` */
  message_group?: string
  /** `/pbxes/:pbxId/switchboards/:extensionDialPlan` */
  switchboard?: string
  /** `/pbxes/:pbxId/extensions/line/:id/edit` */
  line?: string
  /** `/pbxes/:pbxId/system-settings` */
  system_settings?: string
}

const RESOURCE_ID_PLACEHOLDER = /:(groupId|extensionDialPlan|id)\b/

/**
 * Resolve an admin3 path for a blocker entry. Returns `null` when the
 * resource type has no admin3 page available (plain text rendering on the FE)
 * or when the template requires a resource id that the blocker doesn't
 * provide (guards against malformed URLs like `/pbxes/7/messaging/`).
 */
export function buildBlockerLink(
  blocker: DeleteBlocker,
  routePaths: BlockerRoutePaths,
): string | null {
  const template = routePaths[blocker.resource_type as keyof BlockerRoutePaths]
  if (!template) return null

  const resourceId = blocker.resource_id
  if (resourceId == null && RESOURCE_ID_PLACEHOLDER.test(template)) {
    return null
  }

  return template
    .replace(':pbxId', String(blocker.pbx_id))
    .replace(':groupId', resourceId != null ? String(resourceId) : '')
    .replace(':extensionDialPlan', resourceId != null ? String(resourceId) : '')
    .replace(':id', resourceId != null ? String(resourceId) : '')
}
